import axios from 'axios';
import env from 'react-dotenv';
import datas from '../Assets/data.json';
import { Crypter } from './Security/Crypter';
import speech from '../Components/Fonctions/speech';

/**
 * Configuration d'annonce vocale — domaines VOIX_APPEL et MODE_ANNONCE
 * de api/GestionFile/ManageParametres.
 *
 * Portée : la règle la plus précise gagne
 *   service+agence > agence > service > entité > valeur par défaut.
 */

export type ModeAnnonce = 'CENTRALISE' | 'LOCAL' | 'LES_DEUX';

export interface AnnonceConfig {
    /** Vitesse de lecture — 0.50 (lent) .. 1.00 (défaut) .. 2.00 (rapide). */
    vitesse: number;
    /** Qui prononce l'appel : écran (CENTRALISE, défaut), guichet (LOCAL), les deux. */
    mode: ModeAnnonce;
}

const DEFAULT_CONFIG: AnnonceConfig = { vitesse: 1, mode: 'CENTRALISE' };

/** GET lecture seule : { Domaine } — le serveur force LISTER. */
function listerDomaine(authHeader: () => string, domaine: string): Promise<any[]> {
    return axios.request({
        method: 'get',
        maxBodyLength: Infinity,
        url: env.API_BASE_URL + datas.API_LINKs.GestionFile.ManageParametresGet
            + Crypter.encrypt(JSON.stringify({ Domaine: domaine })),
        headers: {
            'Accept': 'text/plain',
            'Authorization': authHeader(),
        }
    })
        .then((response: any) => {
            const res = JSON.parse(Crypter.decrypt(response.data)).Value
            return res.StatutCode == 200 ? (res.Result || []) : []
        })
        .catch(() => []);
}

/** La règle la plus précise pour (CodeFiliale, CodeS) gagne. */
function resolveScope<T extends { CodeFiliale?: any; CodeS?: any }>(
    regles: T[], filiale?: string | null, codeS?: string | null
): T | undefined {
    const score = (r: T) => {
        const fOk = !r.CodeFiliale || r.CodeFiliale == filiale
        const sOk = !r.CodeS || r.CodeS == codeS
        if (!fOk || !sOk) return -1
        return (r.CodeFiliale ? 1 : 0) + (r.CodeS ? 2 : 0) // service pèse plus : f+s=3 > s=2 > f=1 > 0
    }
    return regles
        .map(r => ({ r, s: score(r) }))
        .filter(x => x.s >= 0)
        .sort((a, b) => b.s - a.s)[0]?.r
}

/**
 * Charge la configuration effective d'annonce pour l'agence/service de l'agent.
 * Tolérante : en cas d'échec réseau, retourne les valeurs par défaut
 * (vitesse normale, annonce centralisée).
 */
export async function loadConfigAnnonce(
    authHeader: () => string,
    filiale?: string | null,
    codeS?: string | null,
): Promise<AnnonceConfig> {
    const [voix, modes] = await Promise.all([
        listerDomaine(authHeader, 'VOIX_APPEL'),
        listerDomaine(authHeader, 'MODE_ANNONCE'),
    ])
    const rVoix = resolveScope(voix, filiale, codeS)
    const rMode = resolveScope(modes, filiale, codeS)
    return {
        vitesse: Number(rVoix?.Vitesse) || DEFAULT_CONFIG.vitesse,
        mode: (['CENTRALISE', 'LOCAL', 'LES_DEUX'] as ModeAnnonce[]).includes(rMode?.ModeAnnonce)
            ? rMode.ModeAnnonce
            : DEFAULT_CONFIG.mode,
    }
}

/**
 * Prononce l'appel selon le contexte d'exécution et la configuration.
 * @param contexte 'live' = écran d'appel (TV) ; 'guichet' = poste opérateur.
 * @param vitessePayload  VitesseAppel éventuellement poussée par le hub.
 */
export function annoncerAppel(
    contexte: 'live' | 'guichet',
    mfr: string | null | undefined,
    men: string | null | undefined,
    cfg: AnnonceConfig,
    vitessePayload?: number,
) {
    const parle = contexte === 'live'
        ? cfg.mode !== 'LOCAL'           // CENTRALISE ou LES_DEUX → l'écran parle
        : cfg.mode !== 'CENTRALISE'      // LOCAL ou LES_DEUX → le guichet parle
    if (!parle || (!mfr && !men)) return
    try {
        speech(`${mfr || ''}|${men || ''}`, vitessePayload || cfg.vitesse || 1)
    } catch { }
}
